import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { TenderCase } from "../types"
import type { TenderParticipantRole } from "@/api/tender-api"
import type { DirectorySearchResult } from "@/services/directory-search-service"
import type { Participant } from "@/services/project-service"

import { DirectorySearch } from "@/components/directory-search"
import { Info, Share2, UserPlus } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { ParticipantList } from "@/features/projects/components/participant-list"
import { SettingsService } from "@/services/settings-service"

import {
  useAddTenderParticipant,
  useRemoveTenderParticipant,
  useTenderParticipants,
  useTransferTenderOwnership,
  useUpdateTenderParticipantRole,
} from "../hooks/use-tender-cases"

type AddableRole = Exclude<TenderParticipantRole, "owner">

type TenderShareDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  caseId: string
  tenderCase: TenderCase | undefined
}

export function TenderShareDialog({
  open,
  onOpenChange,
  caseId,
  tenderCase,
}: TenderShareDialogProps) {
  const { t } = useTranslation()
  const [newParticipantRole, setNewParticipantRole] = useState<AddableRole>("reader")

  const currentUserOid = SettingsService.getInstance().claims.oid

  const { data: participantsData, isLoading } = useTenderParticipants(caseId)

  const addParticipant = useAddTenderParticipant()
  const updateParticipantRole = useUpdateTenderParticipantRole()
  const removeParticipant = useRemoveTenderParticipant()
  const transferOwnership = useTransferTenderOwnership()

  const canManage =
    tenderCase?.currentUserRole === "owner" || tenderCase?.currentUserRole === "admin"
  const isAdminFallback = tenderCase?.currentUserAccessSource === "admin_default"

  const existingOids = (participantsData?.participants.map((p) => p.oid) ?? []).filter(
    Boolean
  ) as string[]

  const handleAddParticipant = async (result: DirectorySearchResult) => {
    if (existingOids.includes(result.oid)) return

    await addParticipant.mutateAsync({
      caseId,
      participant: {
        oid: result.oid,
        displayName: result.displayName,
        type: result.type,
        role: newParticipantRole,
      },
    })
  }

  const handleRoleChange = async (participantOid: string, role: TenderParticipantRole) => {
    await updateParticipantRole.mutateAsync({ caseId, participantOid, role })
  }

  const handleRemove = async (participantOid: string) => {
    await removeParticipant.mutateAsync({ caseId, participantOid })
  }

  const handleTransferOwnership = async (newOwnerOid: string) => {
    await transferOwnership.mutateAsync({ caseId, newOwnerOid })
    onOpenChange(false)
  }

  const participants: Participant[] = (participantsData?.participants ?? []).map((p) => ({
    oid: p.oid,
    displayName: p.displayName,
    type: p.type,
    role: p.role,
  }))

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="size-5" />
            {t("tender.share.title")}
          </DialogTitle>
          <DialogDescription>{t("tender.share.description")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {isAdminFallback && (
            <div className="border-warning bg-warning/10 text-warning-foreground rounded-md border px-3 py-2 text-sm">
              <div className="flex items-start gap-2">
                <Info className="mt-0.5 size-4 shrink-0" />
                <div className="space-y-1">
                  <p className="font-medium">{t("tender.share.adminViewTitle")}</p>
                  <p className="text-warning-foreground">{t("tender.share.adminViewBody")}</p>
                </div>
              </div>
            </div>
          )}

          {canManage && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <UserPlus className="text-muted-foreground size-4" />
                <Label className="text-sm font-medium">{t("tender.share.addPeople")}</Label>
              </div>

              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <DirectorySearch
                    // Case participants are colleagues; guests are not offered.
                    membersOnly
                    disabled={addParticipant.isPending}
                    selectedOids={existingOids}
                    onSelect={handleAddParticipant}
                  />
                </div>
                <Select
                  value={newParticipantRole}
                  onValueChange={(v) => setNewParticipantRole(v as AddableRole)}
                >
                  <SelectTrigger className="w-[110px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="reader">{t("tender.roles.reader")}</SelectItem>
                    <SelectItem value="editor">{t("tender.roles.editor")}</SelectItem>
                    <SelectItem value="admin">{t("tender.roles.admin")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />
            </div>
          )}

          <div className="space-y-3">
            <Label className="text-sm font-medium">{t("tender.share.peopleWithAccess")}</Label>
            <ParticipantList
              canManage={canManage}
              currentUserOid={currentUserOid}
              isCurrentUserOwner={tenderCase?.currentUserRole === "owner"}
              isLoading={isLoading}
              ownerUserId={participantsData?.ownerUserId}
              participants={participants}
              onRemove={handleRemove}
              onRoleChange={handleRoleChange}
              onTransferOwnership={handleTransferOwnership}
            />
          </div>

          <div className="bg-muted/50 text-muted-foreground rounded-md px-3 py-2 text-xs">
            <p className="mb-1 font-medium">{t("tender.share.roleLevelsTitle")}</p>
            <ul className="space-y-0.5">
              <li>
                <strong>{t("tender.roles.reader")}:</strong> {t("tender.share.roleReaderDesc")}
              </li>
              <li>
                <strong>{t("tender.roles.editor")}:</strong> {t("tender.share.roleEditorDesc")}
              </li>
              <li>
                <strong>{t("tender.roles.admin")}:</strong> {t("tender.share.roleAdminDesc")}
              </li>
              <li>
                <strong>{t("tender.roles.owner")}:</strong> {t("tender.share.roleOwnerDesc")}
              </li>
            </ul>
          </div>
        </div>

        <div className="flex justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("tender.share.close")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
